import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';

import { useGet } from 'hooks/use-get';
import { CheckCircle, Square, SquareCheckBig } from 'lucide-react';
import Mui from 'materials';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import _ from 'underscore';
import { FishGroupSnapshot as SnapshotModel } from 'views/planning-prognosis/features/view-fish-group/types/fish-group-snapshot.type';
import { FishGroup } from 'views/planning-prognosis/features/view-fish-group/types/fish-group.type';
import { getFishGroupSnapshots } from '../api/fish-group-snapshot/get-fish-group-snapshots';
import Paper from 'components/paper';

type Props = {
  fishGroup: FishGroup;
  isEditable: boolean;
  selectedSnapshot: SnapshotModel | undefined;
  setSelectedSnapshot: React.Dispatch<React.SetStateAction<SnapshotModel | undefined>>;
};

const FishGroupSnapshot: React.FC<Props> = ({ fishGroup, isEditable, selectedSnapshot, setSelectedSnapshot }) => {
  const intl = useIntl();
  const theme = useTheme();
  const PAGE_NAME_ID = 'planningPrognosis.viewFishGroup';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // FETCH DATA
  const { data: snapshots, isLoading: snapshotsIsLoading } = useGet(
    ['getFishGroupSnapshots', fishGroup?.fishGroupId],
    (context) => getFishGroupSnapshots(String(context.queryKey[1])),
    {
      enabled: !!fishGroup?.fishGroupId,
    }
  );

  // HANDLERS
  const handleSelectedSnapshots = (value: SnapshotModel, checked: boolean) => {
    setSelectedSnapshot(checked ? value : undefined);
  };

  return (
    <>
      <Paper transparent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={5}>
          <Typography variant="h3" color={theme.palette.common.white}>
            <FormattedMessage id={`${PAGE_NAME_ID}.snapshotsList.title`} />
          </Typography>
        </Box>

        <List
          sx={{ mt: 4 }}
          options={[
            { id: 'name', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.snapshotsList.nameLabel` }) },
            { id: 'real', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.snapshotsList.realLabel` }), align: 'center' },
            { id: 'plan', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.snapshotsList.planLabel` }), align: 'center' },
            { id: 'prognosis', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.snapshotsList.prognosisLabel` }), align: 'center' },
            {
              id: 'show',
              label: intl.formatMessage({ id: `${PAGE_NAME_ID}.snapshotsList.showLabel` }),
              align: 'center',
              autoWidth: true,
            },
          ]}
          isLoading={snapshotsIsLoading}
          noResult={{
            show: !!snapshots && snapshots?.length <= 0,
            text: <FormattedMessage id={`${PAGE_NAME_ID}.snapshotsList.noResults`} />,
          }}>
          {snapshots?.map((row) => (
            <ListRow key={row.id}>
              <ListCell align="left">{row.name}</ListCell>
              <ListCell align={isMobile ? 'right' : 'center'}>
                <Box>
                  {_.includes(row.dataAvailable, 'real') ? <CheckCircle color={theme.palette.darkBlue.main} size={22} /> : null}
                </Box>
              </ListCell>
              <ListCell align={isMobile ? 'right' : 'center'}>
                <Box>
                  {_.includes(row.dataAvailable, 'plan') ? <CheckCircle color={theme.palette.darkBlue.main} size={22} /> : null}
                </Box>
              </ListCell>
              <ListCell align={isMobile ? 'right' : 'center'}>
                <Box>
                  {_.includes(row.dataAvailable, 'prognosis') ? <CheckCircle color={theme.palette.darkBlue.main} size={22} /> : null}
                </Box>
              </ListCell>
              <ListCell align={isMobile ? 'right' : 'center'}>
                <Box>
                  <Mui.Checkbox
                    disableRipple
                    color="primary"
                    tabIndex={-1}
                    disabled={!isEditable}
                    checked={selectedSnapshot?.id === row.id}
                    icon={<Square size={22} color={theme.palette.darkBlue.main} />}
                    checkedIcon={<SquareCheckBig size={22} color={theme.palette.darkBlue.main} />}
                    onChange={(_, checked) => handleSelectedSnapshots(row, checked)}
                  />
                </Box>
              </ListCell>
            </ListRow>
          ))}
        </List>
      </Paper>
    </>
  );
};

export default FishGroupSnapshot;
